import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';

const aboutImage = 'https://readdy.ai/api/search-image?query=Luxury%20Macau%20resort%20grand%20lobby%20with%20golden%20chandelier%20and%20floral%20decorations%2C%20elegant%20marble%20interior%2C%20warm%20golden%20ambient%20lighting%2C%20asian%20luxury%20hotel%20aesthetic%2C%20stylized%20editorial%20photography%2C%20soft%20cream%20tones%2C%20grand%20staircase&width=1400&height=600&seq=about-header-01&orientation=landscape';

const honorImage1 = 'https://readdy.ai/api/search-image?query=Prestigious%20luxury%20hotel%20award%20trophy%20on%20elegant%20marble%20pedestal%20with%20warm%20golden%20lighting%2C%20refined%20atmosphere%2C%20asian%20luxury%20aesthetic%2C%20stylized%20editorial%20photography%2C%20soft%20cream%20background%2C%20clean%20composition&width=400&height=300&seq=about-honor-01&orientation=landscape';
const honorImage2 = 'https://readdy.ai/api/search-image?query=Five%20star%20hotel%20rating%20certificate%20plaque%20with%20gold%20frame%20on%20elegant%20wall%2C%20warm%20ambient%20lighting%2C%20luxury%20hotel%20atmosphere%2C%20asian%20aesthetic%2C%20stylized%20editorial%20photography%2C%20clean%20composition&width=400&height=300&seq=about-honor-02&orientation=landscape';
const honorImage3 = 'https://readdy.ai/api/search-image?query=World%20luxury%20hotel%20award%20ceremony%20with%20golden%20trophies%20and%20elegant%20floral%20arrangements%2C%20prestigious%20atmosphere%2C%20warm%20lighting%2C%20asian%20luxury%20aesthetic%2C%20stylized%20photography%2C%20soft%20cream%20tones&width=400&height=300&seq=about-honor-03&orientation=landscape';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormState('success');
      setFormMessage('感谢您的留言，我们会尽快回复您！');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    setFormState('submitting');
    try {
      const response = await fetch('https://readdy.ai/api/form/d9u13c78l97n3eas3fhg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        setFormMessage('感谢您的留言，我们会尽快回复您！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormState('success');
          setFormMessage('感谢您的留言，我们会尽快回复您！');
        } else {
          setFormState('error');
          setFormMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormState('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '永利皇宫·官方体验指南',
    url: 'https://embedded-expo.cn',
    description: '永利皇宫是澳门顶级综合度假胜地，汇聚五星级酒店、精致餐饮、高端娱乐与购物体验。',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '体育馆大马路',
      addressLocality: '路氹',
      addressRegion: '澳门',
      addressCountry: 'MO',
    },
    telephone: '+853-8889-8889',
    email: 'info@wynnpalace.com',
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们"
        description="了解永利皇宫体验指南的品牌故事、荣誉口碑与联系方式。我们致力于为您提供最全面的澳门永利皇宫旅游攻略与尊享服务信息。"
        keywords="永利皇宫,关于永利皇宫,永利皇宫品牌,永利皇宫联系,澳门旅游"
        canonicalUrl="https://embedded-expo.cn/about"
        schema={[orgSchema]}
      />

      <Navbar />

      <main>
        <section className="relative w-full h-[320px] md:h-[400px] flex items-end overflow-hidden">
          <img
            src={aboutImage}
            alt="永利皇宫品牌故事"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          <div className="relative z-10 w-full px-4 md:px-6 pb-8 md:pb-10">
            <div className="max-w-6xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
                品牌故事
              </span>
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4">
                关于永利皇宫体验指南
              </h1>
              <p className="text-foreground-600 text-sm leading-relaxed max-w-2xl mx-auto">
                我们是一群热爱澳门、深耕高端酒店与旅游领域的资深体验官，
                致力于为您呈现最真实、最全面的永利皇宫攻略与体验分享
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center mb-16">
              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-4">
                  我们的使命
                </h2>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  永利皇宫作为澳门最顶级的综合度假胜地，其丰富的设施与服务常常让初次到访的旅客感到目不暇接。
                  我们的使命是通过详实、客观、深度的内容，帮助每一位计划前往澳门的旅客，
                  充分了解永利皇宫的方方面面，从而规划出最适合自己的完美假期。
                </p>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  从酒店套房的房型选择到米其林餐厅的预订技巧，
                  从表演湖的最佳观赏位置到名店街的购物攻略，
                  我们事无巨细地为您呈现永利皇宫的每一个精彩细节。
                </p>
                <p className="text-foreground-700 text-sm leading-relaxed">
                  我们的内容团队由资深旅行作家、美食评论家、专业摄影师组成，
                  每位成员都拥有超过5年的澳门旅游行业经验，
                  确保每一篇文章都经过实地体验和专业审核。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden aspect-[4/3] bg-background-100">
                <img
                  src={aboutImage}
                  alt="永利皇宫奢华大堂"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="mb-16">
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-8 text-center">
                荣誉与口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="rounded-xl bg-background-100 border border-secondary-200/60 p-6 text-center">
                  <div className="aspect-[4/3] rounded-lg overflow-hidden mb-4">
                    <img src={honorImage1} alt="Forbes五星大奖" className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    Forbes Travel Guide 五星大奖
                  </h3>
                  <p className="text-foreground-600 text-xs leading-relaxed">
                    永利皇宫连续多年荣获Forbes旅游指南最高五星评级，涵盖酒店、水疗与餐饮多个类别
                  </p>
                </div>
                <div className="rounded-xl bg-background-100 border border-secondary-200/60 p-6 text-center">
                  <div className="aspect-[4/3] rounded-lg overflow-hidden mb-4">
                    <img src={honorImage2} alt="米其林星级认证" className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    米其林星级认证
                  </h3>
                  <p className="text-foreground-600 text-xs leading-relaxed">
                    永利皇宫内多家餐厅荣获米其林星级殊荣，永利宫更是连续多年蝉联米其林二星
                  </p>
                </div>
                <div className="rounded-xl bg-background-100 border border-secondary-200/60 p-6 text-center">
                  <div className="aspect-[4/3] rounded-lg overflow-hidden mb-4">
                    <img src={honorImage3} alt="世界旅游大奖" className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    世界旅游大奖
                  </h3>
                  <p className="text-foreground-600 text-xs leading-relaxed">
                    荣获World Travel Awards「澳门领先度假村」及「亚洲领先度假村」等多项国际大奖
                  </p>
                </div>
              </div>
            </div>

            <div id="contact" className="scroll-mt-20">
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">地址</h4>
                        <p className="text-foreground-600 text-sm">澳门路氹体育馆大马路</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电话</h4>
                        <p className="text-foreground-600 text-sm">+853 8889 8889</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">邮箱</h4>
                        <p className="text-foreground-600 text-sm">info@wynnpalace.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 shrink-0">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">工作时间</h4>
                        <p className="text-foreground-600 text-sm">周一至周日 09:00 - 21:00</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-4">关注我们</h4>
                    <div className="flex items-center gap-3">
                      <a href="#" className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微信" rel="nofollow">
                        <i className="ri-wechat-line text-lg"></i>
                      </a>
                      <a href="#" className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微博" rel="nofollow">
                        <i className="ri-weibo-line text-lg"></i>
                      </a>
                      <a href="#" className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="小红书" rel="nofollow">
                        <i className="ri-chat-smile-2-line text-lg"></i>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl bg-background-100 border border-secondary-200/60 p-6 md:p-8">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                    给我们留言
                  </h3>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    <div className="contact-form-hidden-field">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-foreground-700 text-sm font-medium mb-1.5">姓名</label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-foreground-700 text-sm font-medium mb-1.5">邮箱</label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-foreground-700 text-sm font-medium mb-1.5">主题</label>
                      <input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入留言主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-foreground-700 text-sm font-medium mb-1.5">留言内容</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={formState === 'submitting'}
                      className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
                    >
                      {formState === 'submitting' ? '提交中...' : '发送留言'}
                    </button>

                    {formState === 'success' && (
                      <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm text-center">
                        <i className="ri-check-line mr-1.5"></i>
                        {formMessage}
                      </div>
                    )}
                    {formState === 'error' && (
                      <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm text-center">
                        <i className="ri-close-line mr-1.5"></i>
                        {formMessage}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}